"use client";

import { Camera, Loader2, User } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Image } from "@/components/ui/image";
import { requestAuthToken } from "@/lib/api";
import { getIdToken } from "@/lib/firebase";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";

type Point = {
	x: number;
	y: number;
};

type Area = {
	x: number;
	y: number;
	width: number;
	height: number;
};

const CROP_SIZE = 300;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * BYTES_PER_MB;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
const JPEG_QUALITY = 0.95;
const IMG_SIZE = 64;
const ZOOM_PERCENTAGE_MULTIPLIER = 100;

const createImage = (url: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new window.Image();
		image.addEventListener("load", () => resolve(image));
		image.addEventListener("error", (error: Event) => reject(error));
		image.setAttribute("crossOrigin", "anonymous");
		image.src = url;
	});

const getCroppedImg = async (
	imageSrc: string,
	pixelCrop: Area,
	targetWidth: number,
	targetHeight: number
): Promise<Blob> => {
	const image = await createImage(imageSrc);

	const cropCanvas = document.createElement("canvas");
	const finalCanvas = document.createElement("canvas");
	const cropCtx = cropCanvas.getContext("2d");
	const finalCtx = finalCanvas.getContext("2d");

	if (!(cropCtx && finalCtx)) {
		throw new Error("No 2d context");
	}

	cropCanvas.width = pixelCrop.width;
	cropCanvas.height = pixelCrop.height;

	cropCtx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height
	);

	finalCanvas.width = targetWidth;
	finalCanvas.height = targetHeight;

	finalCtx.fillStyle = "white";
	finalCtx.fillRect(0, 0, targetWidth, targetHeight);

	finalCtx.drawImage(
		cropCanvas,
		0,
		0,
		cropCanvas.width,
		cropCanvas.height,
		0,
		0,
		targetWidth,
		targetHeight
	);

	return new Promise((resolve, reject) => {
		finalCanvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("Canvas is empty"));
					return;
				}
				resolve(blob);
			},
			"image/jpeg",
			JPEG_QUALITY
		);
	});
};

export function ProfileImageUpload() {
	const user = useStore(authStore, (state) => state.user);
	const users = useStore(coreStore, (state) => state.users);
	const currentUserProfile = user?.uid ? users.get(user.uid)?.profile : null;

	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showCropper, setShowCropper] = useState(false);
	const [imageSrc, setImageSrc] = useState<string | null>(null);
	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
		setCroppedAreaPixels(pixels);
	}, []);

	// Profile editing is disabled, because the profile is imported and read-only
	// (docs/adr/25-user-profiles-elaso-owns-the-imported-data.md). Re-enable by
	// removing this flag and the short-circuit below.
	const uploadDisabled = true;
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Upload disabled, and the server returns 403, so stop before any request.
		if (uploadDisabled) {
			if (event.target.value) {
				event.target.value = "";
			}
			return;
		}

		const file = event.target.files?.[0];
		setError(null);

		if (file) {
			if (file.size >= MAX_FILE_SIZE) {
				setError("File is too large. Maximum size is 5 MB.");
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
				return;
			}

			setSelectedFile(file);

			const reader = new FileReader();
			reader.addEventListener("load", () => {
				if (typeof reader.result === "string") {
					setImageSrc(reader.result);
					setShowCropper(true);
				}
			});
			reader.readAsDataURL(file);
		}
	};

	const handleCropCancel = () => {
		setShowCropper(false);
		setImageSrc(null);
		setSelectedFile(null);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleCropConfirm = async () => {
		if (!(imageSrc && croppedAreaPixels && selectedFile)) {
			setError("Cropping failed. Please try again.");
			setShowCropper(false);
			return;
		}

		try {
			const croppedImageBlob = await getCroppedImg(
				imageSrc,
				croppedAreaPixels,
				CROP_SIZE,
				CROP_SIZE
			);

			const firstName = currentUserProfile?.givenName ?? "user";
			const lastName = currentUserProfile?.familyName ?? "profile";
			const timestamp = Date.now();
			const jpegFileName = `${firstName}_${lastName}_profile_image_${timestamp}.jpg`;

			const croppedFile = new File([croppedImageBlob], jpegFileName, {
				type: "image/jpeg",
			});

			setShowCropper(false);
			setIsUploading(true);

			const base64FileData = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(croppedFile);
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = (err) =>
					reject(new Error(`Failed to read file: ${err}`));
			});

			const idToken = await getIdToken();
			if (!idToken) {
				throw new Error("Not authenticated");
			}

			const authToken = await requestAuthToken(idToken);
			const serverUrl =
				import.meta.env.VITE_SERVER_URL || "http://localhost:3000";
			const response = await fetch(`${serverUrl}/api/profile-picture`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${authToken}`,
				},
				body: JSON.stringify({
					fileName: croppedFile.name,
					fileType: croppedFile.type,
					fileData: base64FileData,
				}),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.error || "Upload failed");
			}

			setError(null);
		} catch (err) {
			console.error("Upload error:", err);
			setError("Failed to upload profile picture. Please try again.");
		} finally {
			setIsUploading(false);
			setCrop({ x: 0, y: 0 });
			setZoom(1);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const profilePictureUrl = currentUserProfile?.profilePhoto;

	return (
		<div className="flex flex-col gap-2">
			<span className="font-medium text-sm">Profile Picture</span>
			<p className="text-muted-foreground text-xs">
				Upload/Update your profile picture.
			</p>

			<div className="flex items-center gap-4">
				<div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-border">
					{profilePictureUrl ? (
						<Image
							alt="Profile"
							className="h-full w-full object-cover"
							height={IMG_SIZE}
							src={profilePictureUrl}
							width={IMG_SIZE}
						/>
					) : (
						<div className="flex h-full w-full items-center justify-center bg-muted">
							<User className="size-8 text-muted-foreground" />
						</div>
					)}
				</div>

				<div className="flex flex-col gap-2">
					{/* Profile editing is disabled, because the profile is imported and read-only. */}
					<Button
						className="w-fit"
						disabled
						onClick={() => fileInputRef.current?.click()}
						size="sm"
						variant="outline"
					>
						{isUploading ? (
							<>
								<Loader2 className="mr-2 size-4 animate-spin" />
								Uploading...
							</>
						) : (
							<>
								<Camera className="mr-2 size-4" />
								{profilePictureUrl ? "Change Photo" : "Upload Photo"}
							</>
						)}
					</Button>
					{error && <p className="text-destructive text-xs">{error}</p>}
				</div>

				<input
					accept="image/png, image/jpeg, image/webp"
					className="hidden"
					disabled
					onChange={handleFileChange}
					ref={fileInputRef}
					type="file"
				/>
			</div>

			<Dialog onOpenChange={setShowCropper} open={showCropper}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>Crop Your Image</DialogTitle>
					</DialogHeader>
					<p className="text-muted-foreground text-sm">
						Position your image within the crop area.
					</p>

					{imageSrc && (
						<>
							<div className="relative h-80 w-full overflow-hidden rounded-lg bg-muted">
								<Cropper
									aspect={1}
									crop={crop}
									cropShape="round"
									image={imageSrc}
									maxZoom={MAX_ZOOM}
									minZoom={MIN_ZOOM}
									objectFit="contain"
									onCropChange={setCrop}
									onCropComplete={onCropComplete}
									onZoomChange={setZoom}
									showGrid={false}
									zoom={zoom}
								/>
							</div>

							<div className="flex items-center gap-3">
								<span className="text-muted-foreground text-sm">Zoom:</span>
								<input
									className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-muted"
									max={MAX_ZOOM}
									min={MIN_ZOOM}
									onChange={(e) => setZoom(Number(e.target.value))}
									step={ZOOM_STEP}
									type="range"
									value={zoom}
								/>
								<span className="w-12 text-muted-foreground text-sm">
									{Math.round(zoom * ZOOM_PERCENTAGE_MULTIPLIER)}%
								</span>
							</div>

							<div className="flex justify-end gap-3">
								<Button onClick={handleCropCancel} variant="outline">
									Cancel
								</Button>
								<Button onClick={handleCropConfirm}>Crop & Upload</Button>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
